/**
 * Pure localStorage logic for the Care Dock prompt.
 * Fail-closed: if storage is unavailable, never show.
 */

import {
  CARE_DOCK_COOLDOWN_UNTIL_KEY,
  CARE_DOCK_CTA_COOLDOWN_MS,
  CARE_DOCK_DISMISS_COOLDOWN_MS,
  CARE_DOCK_LAST_SHOWN_AT_KEY,
  CARE_DOCK_RESHOW_AFTER_MS,
  CARE_DOCK_SUCCESS_COUNT_KEY,
  CARE_DOCK_SUCCESS_THRESHOLD,
} from "@/lib/care-dock-keys";

export type CareDockSnapshot = {
  successCount: number;
  lastShownAt: number | null;
  cooldownUntil: number | null;
};

function safeGetItem(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    /* v8 ignore next -- residual branch: callers pre-check availability */
    if (typeof localStorage === "undefined") return false;
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function parseNonNegInt(raw: string | null): number {
  if (raw == null || raw === "") return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseTimestamp(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** True when localStorage can be read/written for Care Dock. */
export function isCareDockStorageAvailable(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    const probe = "__vortixy_care_dock_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function readCareDockSnapshot(): CareDockSnapshot {
  return {
    successCount: parseNonNegInt(safeGetItem(CARE_DOCK_SUCCESS_COUNT_KEY)),
    lastShownAt: parseTimestamp(safeGetItem(CARE_DOCK_LAST_SHOWN_AT_KEY)),
    cooldownUntil: parseTimestamp(safeGetItem(CARE_DOCK_COOLDOWN_UNTIL_KEY)),
  };
}

export function isInCareDockCooldown(now: number = Date.now()): boolean {
  const { cooldownUntil } = readCareDockSnapshot();
  return cooldownUntil != null && now < cooldownUntil;
}

/**
 * After incrementing success count, should the dock become visible?
 * - Not during cooldown
 * - Count ≥ threshold, OR ≥24h since last show and count ≥ 1
 */
export function shouldShowCareDock(snapshot: CareDockSnapshot, now: number = Date.now()): boolean {
  if (snapshot.cooldownUntil != null && now < snapshot.cooldownUntil) {
    return false;
  }
  if (snapshot.successCount >= CARE_DOCK_SUCCESS_THRESHOLD) {
    return true;
  }
  if (
    snapshot.lastShownAt != null &&
    now - snapshot.lastShownAt >= CARE_DOCK_RESHOW_AFTER_MS &&
    snapshot.successCount >= 1
  ) {
    return true;
  }
  return false;
}

/**
 * Record a successful tool run. Returns whether the Care Dock should open now.
 * Fail-closed if storage is unavailable.
 */
export function recordCareDockSuccess(now: number = Date.now()): { shouldShow: boolean } {
  if (!isCareDockStorageAvailable()) {
    return { shouldShow: false };
  }

  // Do not count successes while silenced — user asked for space.
  if (isInCareDockCooldown(now)) {
    return { shouldShow: false };
  }

  const prev = readCareDockSnapshot();
  const successCount = prev.successCount + 1;
  if (!safeSetItem(CARE_DOCK_SUCCESS_COUNT_KEY, String(successCount))) {
    return { shouldShow: false };
  }

  const next: CareDockSnapshot = { ...prev, successCount };
  if (!shouldShowCareDock(next, now)) {
    return { shouldShow: false };
  }

  // Persist "shown" state: reset count, stamp lastShownAt
  safeSetItem(CARE_DOCK_SUCCESS_COUNT_KEY, "0");
  safeSetItem(CARE_DOCK_LAST_SHOWN_AT_KEY, String(now));
  return { shouldShow: true };
}

/** User closed with × / Escape → 7-day cooldown. */
export function dismissCareDock(now: number = Date.now()): void {
  if (!isCareDockStorageAvailable()) return;
  safeSetItem(CARE_DOCK_COOLDOWN_UNTIL_KEY, String(now + CARE_DOCK_DISMISS_COOLDOWN_MS));
  safeSetItem(CARE_DOCK_SUCCESS_COUNT_KEY, "0");
}

/** User clicked a CTA → 14-day cooldown. */
export function markCareDockCta(now: number = Date.now()): void {
  if (!isCareDockStorageAvailable()) return;
  safeSetItem(CARE_DOCK_COOLDOWN_UNTIL_KEY, String(now + CARE_DOCK_CTA_COOLDOWN_MS));
  safeSetItem(CARE_DOCK_SUCCESS_COUNT_KEY, "0");
}
